import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import MetricGrid from "@/components/MetricGrid";
import { MaterialNames } from "@/components/material_symbol_names";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import { useNoteDeepLink } from "@/features/global/components/NotesTable/useNoteDeepLink";
import MembershipHistoryTable from "@/features/person/components/MembershipHistoryTable";
import type { MembershipHistoryRow } from "@/features/person/components/MembershipHistoryTable";
import { buildProgramStatusCard } from "@/features/person/utils/utils";
import { useDuesStatus } from "@/hooks/useDuesStatus";
import useCapabilities, {
  useCanDeleteMemberBands,
  useCanManageMemberBands,
  useCanManageSuspensions,
  useCanManageSuspensionsRestricted,
  DOMAIN,
  PERMISSION,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";
import { toUTCFormat } from "@/lib/dateHelpers";
import { scrollToElement } from "@/lib/scrollHelpers";
import type { Path } from "@/router";
import { hasDuesPayingProgram } from "@/pages/people/_utils";
import type { Person } from "@/types";
import {
  NoteNotableValues as NoteNotableValueEnum,
  ProgramStatus as ProgramStatusEnum,
  ProgramTypeName
} from "@/types";
import { GET_MEMBERSHIP_DETAILS_QUERY } from "../person/queries";
import ActiveYearsBreakdownModal from "./_components/ActiveYearsBreakdownModal";
import useActiveYearsBreakdown from "./_components/useActiveYearsBreakdown";
import DeleteMembershipModal from "./_components/DeleteMembershipModal";
import EditMembershipModal from "./_components/EditMembershipModal";
import SuspensionHistoryTable from "./_components/SuspensionHistoryTable";
import UpdateMembershipModal from "./_components/UpdateMembershipModal";
import { usePersonMembershipAccess } from "../person/_usePersonMembershipAccess";

type PersonData = { person: Person };

export type { MembershipHistoryRow as MembershipRow } from "@/features/person/components/MembershipHistoryTable";

export default function MembershipDetails() {
  const { pgaId } = useParams();
  const { loaded, canViewPersonMembership } = usePersonMembershipAccess();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [editMembershipModalOpen, setEditMembershipModalOpen] = useState(false);
  const [selectedMembershipRow, setSelectedMembershipRow] = useState<MembershipHistoryRow | null>(null);
  const [updateMembershipModalOpen, setUpdateMembershipModalOpen] = useState(false);
  const [deleteMembershipModalOpen, setDeleteMembershipModalOpen] = useState(false);
  const [selectedBandIdForDeletion, setSelectedBandIdForDeletion] = useState<string | null>(null);
  const [addNoteBandId, setAddNoteBandId] = useState<string | null>(null);

  const canManageMemberBands = useCanManageMemberBands();
  const canDeleteMemberBands = useCanDeleteMemberBands();
  const canManageSuspensions = useCanManageSuspensions();
  const canManageSuspensionsRestricted = useCanManageSuspensionsRestricted();
  const { id: currentUserId } = useCapabilities();
  const canEditMembership = useCanManageMemberBands();
  const canViewPersonBilling = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonBilling
  });
  const canViewOrders = useHasCapabilityBool({
    domain: DOMAIN.COMMERCE,
    permission: PERMISSION.viewOrders
  });

  const {
    data,
    loading,
    error,
    refetch: refetchMembershipDetails,
    startPolling: startMembershipPolling,
    stopPolling: stopMembershipPolling
  } = useQuery<PersonData>(GET_MEMBERSHIP_DETAILS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !loaded || !canViewPersonMembership || !pgaId
  });
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshMembershipAfterSuspensionMutation = useCallback(async () => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    stopMembershipPolling();
    await refetchMembershipDetails();

    startMembershipPolling(3000);
    pollingTimeoutRef.current = setTimeout(() => {
      stopMembershipPolling();
      pollingTimeoutRef.current = null;
    }, 30000);
  }, [refetchMembershipDetails, startMembershipPolling, stopMembershipPolling]);

  const person = data?.person || ({} as Person);
  const { breakdownOpen, openBreakdown, closeBreakdown, activeYearsValue } = useActiveYearsBreakdown(person);
  const { duesMetric } = useDuesStatus(person, {
    skip: !canViewPersonBilling,
    skipOrders: !canViewOrders
  });
  const { bands, membershipSince, latestProgram } = data?.person ?? {};
  const programTypeName = latestProgram?.programType?.name;

  const {
    expandRowId: highlightBandId,
    highlightNoteId,
    tableRef: membershipTableRef
  } = useNoteDeepLink({ dataReady: (bands?.length ?? 0) > 0 });

  const suspensions = person.ccmsSuspensions || [];
  const { expandRowId: highlightSuspensionId, highlightNoteId: suspensionHighlightNoteId } = useNoteDeepLink({
    dataReady: suspensions.length > 0,
    expandParam: "expandSuspensionNotes",
    highlightParam: "highlightSuspensionNote"
  });

  useEffect(() => {
    const scrollTo = searchParams.get("scrollTo");
    if (scrollTo && !loading) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("scrollTo");
      window.history.replaceState({}, "", currentUrl.toString());

      requestAnimationFrame(() => {
        scrollToElement(scrollTo);
      });
    }
  }, [searchParams, loading]);

  useEffect(() => {
    return () => {
      stopMembershipPolling();
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [stopMembershipPolling]);

  if (!loaded || !canViewPersonMembership) {
    return null;
  }

  if (error) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Membership Details"
        message={error.message}
        className="mt-12"
      />
    );
  }

  const primaryEmployment = person.employments?.find((e) => e.isPrimary);
  const membershipClassification = latestProgram?.band?.classification?.name;
  const jobClassification = primaryEmployment?.jobClassification;

  const programSinceFormatted = toUTCFormat(membershipSince || "");
  const isMemberAssociateOrAffiliate =
    programTypeName === ProgramTypeName.Member ||
    programTypeName === ProgramTypeName.Associate ||
    programTypeName === ProgramTypeName.Affiliate;

  const showActiveYearsOnly = programTypeName === ProgramTypeName.Member && activeYearsValue != null;

  const metrics: MetricCardType<Path>[] = isMemberAssociateOrAffiliate
    ? [
        ...(showActiveYearsOnly
          ? []
          : [
              {
                label: `${programTypeName} Since`,
                value: programSinceFormatted,
                iconName: "badge" as const
              }
            ]),
        ...(programTypeName === ProgramTypeName.Member && activeYearsValue != null
          ? [
              {
                label: programSinceFormatted
                  ? `Active Years of Membership\nElection Date: ${programSinceFormatted}`
                  : "Active Years of Membership",
                value: activeYearsValue,
                iconName: "schedule" as const,
                onClick: openBreakdown,
                tooltip: showActiveYearsOnly
                  ? {
                      ...(programSinceFormatted && { title: `Member since ${programSinceFormatted}` }),
                      supportingText: "View how each membership band contributes to this total."
                    }
                  : { supportingText: "View how each membership band contributes to this total." }
              }
            ]
          : []),
        {
          label: "Classification",
          value: membershipClassification,
          iconName: "badge",
          ...(pgaId && {
            onClick: () => navigate(`/people/${pgaId}/career/employment` as any)
          }),
          ...(jobClassification &&
            membershipClassification &&
            jobClassification !== membershipClassification && {
              tooltip: { supportingText: `Employment Classification: ${jobClassification}` }
            })
        },
        {
          ...buildProgramStatusCard(
            latestProgram?.status,
            person.ccmsSuspensions,
            latestProgram?.band?.terminatedReason,
            latestProgram?.band?.terminatesOn,
            "Membership Status"
          ),
          iconName: "badge",
          ...(latestProgram?.status === ProgramStatusEnum.SUSPENDED && {
            scrollToId: "suspension-history"
          })
        } as MetricCardType<Path>,
        ...(hasDuesPayingProgram(person) && canViewPersonBilling && duesMetric ? [duesMetric] : [])
      ]
    : [];

  return (
    <div className={metrics.length ? "flex flex-col gap-6" : undefined}>
      {!!metrics.length && <MetricGrid metrics={metrics} layout="expanded" />}
      {!metrics.length && <div className="pt-12" />}
      <MembershipHistoryTable
        showHeading={false}
        headingTopSpacing="none"
        bands={bands ?? []}
        loading={loading}
        pgaId={pgaId || ""}
        primaryBandId={latestProgram?.band?.id}
        refetchQueries={[GET_MEMBERSHIP_DETAILS_QUERY]}
        highlightBandId={highlightBandId}
        highlightNoteId={highlightNoteId}
        tableRef={membershipTableRef}
        headingActions={
          canManageMemberBands && programTypeName === ProgramTypeName.Member
            ? [
                {
                  label: "Update Membership",
                  icon: "add" as MaterialNames,
                  variant: "outlined" as const,
                  onClick: () => setUpdateMembershipModalOpen(true),
                  testId: "update-membership-button"
                }
              ]
            : []
        }
        actions={{
          canAddNote: canManageMemberBands,
          canEditMembership,
          canDeleteMembership: canDeleteMemberBands,
          onAddNote: setAddNoteBandId,
          onEditMembership: (row) => {
            setSelectedMembershipRow(row);
            setEditMembershipModalOpen(true);
          },
          onDeleteMembership: (bandId) => {
            setSelectedBandIdForDeletion(bandId);
            setDeleteMembershipModalOpen(true);
          }
        }}
      />

      <SuspensionHistoryTable
        suspensions={suspensions}
        loading={loading}
        canEdit={canManageMemberBands}
        canAddSuspension={canManageSuspensions}
        canManageSuspensions={canManageSuspensions}
        canManageSuspensionsRestricted={canManageSuspensionsRestricted}
        pgaId={pgaId}
        personId={person.id}
        refetchQueries={[GET_MEMBERSHIP_DETAILS_QUERY]}
        isPersonActive={latestProgram?.status === ProgramStatusEnum.ACTIVE}
        currentUserId={currentUserId || undefined}
        autoExpandSuspensionId={highlightSuspensionId}
        highlightNoteId={suspensionHighlightNoteId}
        onSuspensionMutationSuccess={refreshMembershipAfterSuspensionMutation}
      />
      {editMembershipModalOpen && (
        <EditMembershipModal
          open={editMembershipModalOpen}
          onClose={() => {
            setEditMembershipModalOpen(false);
            setSelectedMembershipRow(null);
          }}
          membershipRow={selectedMembershipRow}
          personId={person.id}
          pgaId={pgaId || ""}
        />
      )}

      {updateMembershipModalOpen && person.id && pgaId && (
        <UpdateMembershipModal
          open={updateMembershipModalOpen}
          onClose={() => setUpdateMembershipModalOpen(false)}
          person={person}
          pgaId={pgaId}
        />
      )}

      {deleteMembershipModalOpen && selectedBandIdForDeletion && pgaId && (
        <DeleteMembershipModal
          open={deleteMembershipModalOpen}
          onClose={() => {
            setDeleteMembershipModalOpen(false);
            setSelectedBandIdForDeletion(null);
          }}
          bandId={selectedBandIdForDeletion}
          pgaId={pgaId}
        />
      )}

      {breakdownOpen && pgaId && (
        <ActiveYearsBreakdownModal
          open={breakdownOpen}
          onClose={closeBreakdown}
          pgaId={pgaId}
          activeYearsValue={activeYearsValue}
        />
      )}

      {addNoteBandId && (
        <AddEditNoteSideSheet
          open={!!addNoteBandId}
          onClose={() => setAddNoteBandId(null)}
          notableId={addNoteBandId}
          notableType={NoteNotableValueEnum.MEMBERSHIP_BAND}
          refetchQueries={[GET_MEMBERSHIP_DETAILS_QUERY]}
          currentUserId={currentUserId || undefined}
          pgaId={pgaId}
          objectType="person"
        />
      )}
    </div>
  );
}
